import type { ReactNode } from 'react'
import { Sheet } from './Sheet'

/**
 * A wide sheet with numbered steps and a footer that never leaves. Used by the
 * new-project wizard, the schedule import and the plan chooser.
 *
 * Two rules the shell enforces because they are easy to lose one screen at a
 * time: the footer note is the *current step's* guidance, not a standing
 * disclaimer, and the primary button states the consequence — "Pay £23.38",
 * "Create the project" — never "Next" on the last step.
 */

export interface WizardStep {
  label: string
}

interface WizardShellProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  /** A 15px outline glyph before the title. */
  glyph?: ReactNode
  steps: WizardStep[]
  /** 0-based. Everything before it is done, everything after pending. */
  current: number
  children: ReactNode
  /** The step's own guidance. */
  note?: ReactNode
  /** Back, Cancel — whatever sits left of the primary. */
  secondary?: ReactNode
  /** The one committing action, labelled with its consequence. */
  primary?: ReactNode
  width?: number
  top?: number
  bottomRoom?: number
  bodyPadding?: number | string
  /** Amounts inside this sheet are shown even while the curtain is drawn —
   *  for the two wizards where you are typing figures in on purpose.
   *
   *  It has to be the sheet rather than the page: a wizard opens *over* an
   *  index (see ContractsController#new, which renders :index), so the page
   *  underneath is emphatically still covered and only this is not. */
  moneyOpen?: boolean
}

export function WizardShell({
  open,
  onClose,
  title,
  subtitle,
  glyph,
  steps,
  current,
  children,
  note,
  secondary,
  primary,
  width = 760,
  top = 70,
  bottomRoom = 110,
  bodyPadding = 18,
  moneyOpen = false,
}: WizardShellProps) {
  return (
    <Sheet
      moneyOpen={moneyOpen}
      open={open}
      onClose={onClose}
      variant="wide"
      width={width}
      top={top}
      bottomRoom={bottomRoom}
      title={
        glyph ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {glyph}
            {title}
          </span>
        ) : (
          title
        )
      }
      subtitle={subtitle}
      body="bare"
      bodyStyle={{ padding: bodyPadding, display: 'flex', flexDirection: 'column', gap: 14 }}
      footerNote={note}
      footer={
        <>
          {secondary}
          {primary}
        </>
      }
    >
      <StepBar steps={steps} current={current} />
      {/* One non-shrinking child, rather than the step's cards as flex items of
          the body itself. The body is a flex column that scrolls, and a card
          put straight into it is a flex item — so on a short screen the cards
          *compressed* to fit instead of the body scrolling, and `overflow:
          hidden` on Card silently cut whatever no longer fit: the new-contract
          wizard's preset chips and its last payment row were both simply gone
          at 375px, with nothing to scroll to reach them. Found 2026-09-02. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 'none' }}>
        {children}
      </div>
    </Sheet>
  )
}

/**
 * Hidden on a phone (`hide-xs`): at 390px five steps and their labels are
 * unreadable, and the footer's own guidance is already saying where you are.
 *
 * It used to say `hide-sm`, which is "hide below the tablet break" — so the
 * bar was also absent through the whole 768–1100px range, where it fits with
 * room to spare and a wizard was left with no sense of progress at all.
 */
function StepBar({ steps, current }: { steps: WizardStep[]; current: number }) {
  return (
    <div
      className="hide-xs"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        // Cancels the body's own padding, so the bar reads as a strip across
        // the sheet rather than a box floating inside it.
        margin: '-18px -18px 0',
        padding: '11px 18px',
        borderBottom: '1px solid var(--w-line)',
        background: 'var(--w-sunk)',
        flex: 'none',
      }}
    >
      {steps.map((step, i) => {
        const done = i < current
        const active = i === current
        const lit = done || active
        return (
          <div key={step.label} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 99,
                border: lit ? '1px solid transparent' : '1px solid var(--w-quiet)',
                background: lit ? 'var(--color-accent)' : 'transparent',
                font: '600 11px/16px Manrope,sans-serif',
                color: lit ? 'var(--w-onaccent)' : 'var(--color-muted)',
                textAlign: 'center',
                flex: 'none',
                boxSizing: 'border-box',
              }}
            >
              {i + 1}
            </span>
            <span
              style={{
                font: '600 11px Manrope,sans-serif',
                color: active ? 'var(--color-text)' : 'var(--color-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {step.label}
            </span>
            <div
              style={{
                flex: 1,
                height: 1,
                background: done ? 'var(--color-accent)' : 'var(--w-line-soft)',
                minWidth: 12,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
